/**
 * Expanded detail for an internal rebook-workflow milestone root (`kind:
 * "rebook_progress"`). Renders a step header (what happened + who / which agent)
 * above the single booking's pricing comparison, reusing the same hydrated row
 * the opportunity card uses so the full lifecycle (found -> accepted ->
 * confirmed) reads consistently on the feed.
 *
 * Distinct from `FeedRebookConfirmDetail` (external rebooks detected from
 * ingested agent data). Shares the "Rebook" feed slice with opportunities.
 */
import { useUserPreferences } from "@/context/UserPreferencesContext";
import { useDateFormatter } from "@/hooks/useDateFormatter";
import { usePortNames } from "@/hooks/usePortNames";
import { extractLaneLocodes } from "@/lib/formatPort";
import { cn } from "@/lib/utils";
import type {
  RebookProgressPayload,
  RebookProgressRole,
  RebookProgressStep,
} from "@/types/feed";
import {
  DigestBookingRow,
  useHydratedDigestBookings,
} from "./FeedCommercialDigestDetail";

/** Whether a payload is a rebook-progress milestone root. */
export function isRebookProgressPayload(
  payload: unknown,
): payload is RebookProgressPayload {
  if (!payload || typeof payload !== "object") return false;
  const p = payload as Record<string, unknown>;
  return (
    p.kind === "rebook_progress" &&
    !!p.booking &&
    typeof p.booking === "object"
  );
}

/** Human label for the share-viewer role of an agent acknowledgement. */
function roleLabel(role?: RebookProgressRole): string {
  switch (role) {
    case "target":
      return "New agent confirmed the booking request";
    case "source":
      return "Original agent confirmed the cancellation";
    default:
      return "Agent confirmed the rebook";
  }
}

/** Short step headline for the milestone. */
function stepHeadline(step: RebookProgressStep, role?: RebookProgressRole): string {
  switch (step) {
    case "accepted":
      return "Accepted";
    case "agent_acknowledged":
      return roleLabel(role);
    case "cancellation_sent":
      return "Cancellation sent to original agent";
    case "acceptance_cancelled":
      return "Rebook cancelled";
    default:
      return "Rebook update";
  }
}

export function FeedRebookProgressDetail({
  payload,
}: {
  payload: RebookProgressPayload;
}) {
  const { preferences } = useUserPreferences();
  const { formatDate } = useDateFormatter();
  const bookings = useHydratedDigestBookings([payload.booking]);
  const laneLocodes = extractLaneLocodes(...bookings.map((b) => b.lane_key));
  const portNames = usePortNames(laneLocodes);

  const isRebook = payload.dimension === "rebook_opportunity";
  const booking = bookings[0];
  if (!booking) return null;

  const headline = stepHeadline(payload.step, payload.role);
  const actor =
    payload.step === "accepted" || payload.step === "acceptance_cancelled"
      ? payload.actor_name
      : undefined;

  return (
    <div
      className={cn(
        "feed-digest",
        isRebook ? "feed-digest--rebook" : "feed-digest--first-book",
      )}
    >
      <p className="feed-digest__summary feed-row-detail-body__heading">
        {headline}
        {actor ? ` · ${actor}` : ""}
      </p>
      <div className="feed-digest__list">
        <DigestBookingRow
          booking={booking}
          isRebook={isRebook}
          showLocodes={preferences.showLocodes}
          portNames={portNames}
          formatDate={formatDate}
        />
      </div>
    </div>
  );
}
